import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Terms of Service
            </h1>
            <p className="text-xl text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="space-y-8">
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-medium">BETA</span>
                  Beta Phase Notice
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  <strong>Airing Down is currently in Beta.</strong> By using this platform during the Beta phase, 
                  you acknowledge and agree to the following:
                </p>
                <ul>
                  <li><strong>Changes Without Notice:</strong> Features, functionality, and the user interface may change at any time without prior notice as we iterate and improve the platform.</li>
                  <li><strong>Data Preservation:</strong> While we strive to protect your data, we do not guarantee that your data (including GPX files, photos, and activity history) will be preserved during the Beta phase. Major platform updates may require data resets.</li>
                  <li><strong>Service Availability:</strong> The platform may experience downtime, bugs, or unexpected behavior. We do not guarantee uptime or uninterrupted access during Beta.</li>
                  <li><strong>Pricing Changes:</strong> Beta pricing, promotional offers, and subscription tiers are subject to change when the Beta phase ends.</li>
                  <li><strong>Feature Availability:</strong> Some features may be incomplete, experimental, or removed entirely based on user feedback and development priorities.</li>
                </ul>
                <p>
                  We recommend maintaining your own backups of important GPX files and data. Your continued use 
                  of Airing Down during the Beta phase constitutes acceptance of these limitations.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>1. Acceptance of Terms</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  By accessing and using Airing Down, you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please 
                  do not use this service.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>2. Use License</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  Permission is granted to temporarily use Airing Down for personal, non-commercial 
                  transitory viewing only. This is the grant of a license, not a transfer of title, 
                  and under this license you may not:
                </p>
                <ul>
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose</li>
                  <li>Attempt to reverse engineer any software contained on the website</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>3. Trail Safety and Liability</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  <strong>IMPORTANT:</strong> Off-road activities carry inherent risks. Users acknowledge that:
                </p>
                <ul>
                  <li>Trail conditions can change rapidly due to weather, maintenance, or other factors</li>
                  <li>GPS data and trail information may not be 100% accurate</li>
                  <li>Users are responsible for their own safety and preparedness</li>
                  <li>Airing Down is not liable for any injuries, damages, or losses incurred while using trail information</li>
                  <li>Users should always inform others of their plans and carry appropriate safety equipment</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>4. User Content</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  Users retain ownership of content they upload (GPX files, photos, reviews) but grant 
                  Airing Down a license to use, display, and share this content within the platform. 
                  Users are responsible for ensuring they have the right to share uploaded content.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>5. Aggregated and Anonymous Data</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  We may use your contributed data (including GPX tracks, trail feedback, and activity metrics) 
                  in aggregated and anonymized form to:
                </p>
                <ul>
                  <li>Generate trail statistics, popularity metrics, and condition reports</li>
                  <li>Improve trail recommendations and platform features</li>
                  <li>Create public datasets or visualizations that do not identify individual users</li>
                  <li>Provide insights to land management agencies and trail organizations</li>
                </ul>
                <p>
                  <strong>Important:</strong> Aggregated data never reveals individual track details, user identities, 
                  or specific run locations. Private and club-only runs contribute only to anonymized aggregate 
                  statistics (such as "X runs completed on this trail this month") and are never displayed 
                  individually to unauthorized users.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>6. Privacy</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  Your privacy is important to us. Please review our Privacy Policy, which also governs 
                  your use of the service, to understand our practices.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>7. Modifications</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  Airing Down may revise these terms of service at any time without notice. By using 
                  this platform, you are agreeing to be bound by the then current version of these terms.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>8. Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  If you have any questions about these Terms of Service, please contact us at 
                  legal@airingdown.com.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Terms;